import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useLingui } from '@lingui/react';
import { msg } from '@lingui/core/macro';

import { ApiError } from '@/api/core/ApiError';
import type { access_AccessRoleResponse } from '@/api/models/access_AccessRoleResponse';
import { AccessRolesService } from '@/api/services/AccessRolesService';
import { accessKeys } from '@/contexts/AccessContext';
import { useAuthContext } from '@/contexts/AuthContext';
import { useIsAdmin } from '@/hooks/useIsAdmin';
import { extractBackendError, translateBackendError } from '@/lib/translateBackendError';

export type AccessRole = access_AccessRoleResponse;

/** Query-key factory of the Settings -> Access roles page (issue #1670). */
export const accessRoleKeys = {
  all: ['access-roles'] as const,
  list: (orgId: string) => [...accessRoleKeys.all, orgId, 'list'] as const,
  modules: (orgId: string) => [...accessRoleKeys.all, orgId, 'modules'] as const,
  employees: (orgId: string, value: string) => [...accessRoleKeys.all, orgId, 'employees', value] as const,
};

/**
 * Max length of a role label; the same limit as the backend DTO
 * (`internal/handlers/access/dto.go`, `validate:"required,min=1,max=100"`).
 */
export const ACCESS_ROLE_LABEL_MAX = 100;

/** Toast id shared by every "role no longer exists" message, so two sources show one toast. */
export const ACCESS_ROLE_NOT_FOUND_TOAST_ID = 'access-role-not-found';

const FAILED_MESSAGE = msg({
  id: 'accessRoles.toast.failed',
  message: 'The change could not be saved',
});

/**
 * The shared gate of every access-role hook. The endpoints are admin only, so
 * a non-admin issues no request at all; the server (`RequireAdmin`) is the
 * boundary, this is UX.
 */
function useAccessRoleGate() {
  const isAdmin = useIsAdmin();
  const { isSignedIn, isAuthReady, orgId } = useAuthContext();
  const ready = isAdmin && Boolean(isSignedIn) && isAuthReady && Boolean(orgId);
  return { ready, orgId: orgId ?? '' };
}

function assertReady(ready: boolean): void {
  if (!ready) {
    throw new Error('Access roles: not signed in as an administrator');
  }
}

/** Every role (active and inactive) with its grants and employee count. */
export function useAccessRoles() {
  const { ready, orgId } = useAccessRoleGate();
  return useQuery({
    queryKey: accessRoleKeys.list(orgId),
    queryFn: () => AccessRolesService.getApiV1AccessRoles(),
    enabled: ready,
  });
}

/** The grantable module catalog in catalog order. */
export function useAccessModuleCatalog() {
  const { ready, orgId } = useAccessRoleGate();
  return useQuery({
    queryKey: accessRoleKeys.modules(orgId),
    queryFn: () => AccessRolesService.getApiV1AccessRolesModules(),
    enabled: ready,
    staleTime: 10 * 60 * 1000,
  });
}

/**
 * The employees whose contract covering today carries the role (the people
 * behind `employeeCount`). Fetched only while `enabled`, i.e. once the list is opened.
 */
export function useAccessRoleEmployees(value: string, enabled: boolean) {
  const { ready, orgId } = useAccessRoleGate();
  return useQuery({
    queryKey: accessRoleKeys.employees(orgId, value),
    queryFn: () => AccessRolesService.getApiV1AccessRolesEmployees(value),
    enabled: ready && enabled && value !== '',
  });
}

function invalidateAccess(queryClient: QueryClient): void {
  // The module catalog is static; only the role lists change.
  void queryClient.invalidateQueries({
    queryKey: accessRoleKeys.all,
    predicate: (query) => query.queryKey[2] !== 'modules',
  });
  void queryClient.invalidateQueries({ queryKey: accessKeys.all });
  void queryClient.invalidateQueries({ queryKey: ['effective-enums'] });
  // The role label source of useProfessionalRoleEnums.
  void queryClient.invalidateQueries({ queryKey: ['settings', 'effective'] });
  // The Customizations → Employees → Role tab (useHierarchicalSettings).
  void queryClient.invalidateQueries({ queryKey: ['settings', 'hierarchy'] });
}

/** Writes one role into the cached list so the page sees it before the refetch lands. */
function upsertCachedRole(queryClient: QueryClient, orgId: string, role: AccessRole): void {
  queryClient.setQueryData<AccessRole[]>(accessRoleKeys.list(orgId), (old) => {
    if (!old) return old;
    const index = old.findIndex((r) => r.value === role.value);
    if (index === -1) return [...old, role];
    const next = [...old];
    next[index] = role;
    return next;
  });
}

/** True for the 409 `ACCESS_ROLE_IN_USE` answer of `DELETE /access-roles/:value`. */
export function isAccessRoleInUseError(error: unknown): boolean {
  const { code } = extractBackendError(error);
  return code === 'ACCESS_ROLE_IN_USE' || (error instanceof ApiError && error.status === 409);
}

function useAccessRoleErrorHandler() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();
  return (error: unknown) => {
    const { code } = extractBackendError(error);
    const text = translateBackendError(code, i18n, FAILED_MESSAGE);
    if (code === 'ACCESS_ROLE_NOT_FOUND') {
      // The module catalog is static; only the role lists change.
  void queryClient.invalidateQueries({
    queryKey: accessRoleKeys.all,
    predicate: (query) => query.queryKey[2] !== 'modules',
  });
      toast.error(text, { id: ACCESS_ROLE_NOT_FOUND_TOAST_ID });
      return;
    }
    toast.error(text);
  };
}

/** `PUT /access-roles/:value/modules` with the full grant set. */
export function useReplaceAccessRoleModules() {
  const { ready, orgId } = useAccessRoleGate();
  const queryClient = useQueryClient();
  const { i18n } = useLingui();
  const onError = useAccessRoleErrorHandler();
  return useMutation({
    mutationFn: ({ value, modules }: { value: string; modules: string[] }) => {
      assertReady(ready);
      return AccessRolesService.putApiV1AccessRolesModules(value, { modules });
    },
    onSuccess: (role) => {
      upsertCachedRole(queryClient, orgId, role);
      invalidateAccess(queryClient);
      toast.success(
        i18n._(
          msg({
            id: 'accessRoles.toast.saved',
            message: 'Access updated. It applies on the next page load for affected employees.',
          }),
        ),
      );
    },
    onError,
  });
}

/** `POST /access-roles`; `copyFrom` copies the grants of that role. */
export function useCreateAccessRole() {
  const { ready, orgId } = useAccessRoleGate();
  const queryClient = useQueryClient();
  const { i18n } = useLingui();
  const onError = useAccessRoleErrorHandler();
  return useMutation({
    mutationFn: ({ label, copyFrom }: { label: string; copyFrom?: string }) => {
      assertReady(ready);
      return AccessRolesService.postApiV1AccessRoles({
        label: label.trim(),
        ...(copyFrom ? { copyFrom } : {}),
      });
    },
    onSuccess: (role) => {
      upsertCachedRole(queryClient, orgId, role);
      invalidateAccess(queryClient);
      toast.success(i18n._(msg({ id: 'accessRoles.toast.created', message: 'Role created' })));
    },
    onError,
  });
}

/**
 * `PATCH /access-roles/:value` with the trimmed label. `silent` suppresses the
 * success toast for a caller that saves the name with the grants and reports once.
 */
export function useRenameAccessRole(options?: { silent?: boolean }) {
  const silent = options?.silent ?? false;
  const { ready, orgId } = useAccessRoleGate();
  const queryClient = useQueryClient();
  const { i18n } = useLingui();
  const onError = useAccessRoleErrorHandler();
  return useMutation({
    mutationFn: ({ value, label }: { value: string; label: string }) => {
      assertReady(ready);
      return AccessRolesService.patchApiV1AccessRoles(value, { label: label.trim() });
    },
    onSuccess: (role) => {
      upsertCachedRole(queryClient, orgId, role);
      invalidateAccess(queryClient);
      if (!silent) {
        toast.success(i18n._(msg({ id: 'accessRoles.toast.renamed', message: 'Role renamed' })));
      }
    },
    onError,
  });
}

/**
 * `DELETE /access-roles/:value` (deactivates, never deletes).
 *
 * A 409 (`ACCESS_ROLE_IN_USE`) is NOT toasted here: the caller refetches the
 * list and shows the message with the fresh employee count.
 */
export function useDeactivateAccessRole() {
  const { ready, orgId } = useAccessRoleGate();
  const queryClient = useQueryClient();
  const { i18n } = useLingui();
  const onError = useAccessRoleErrorHandler();
  return useMutation({
    mutationFn: (value: string) => {
      assertReady(ready);
      return AccessRolesService.deleteApiV1AccessRoles(value);
    },
    onSuccess: (_result, value) => {
      queryClient.setQueryData<AccessRole[]>(accessRoleKeys.list(orgId), (old) =>
        old?.map((r) => (r.value === value ? { ...r, isActive: false } : r)),
      );
      invalidateAccess(queryClient);
      toast.success(
        i18n._(msg({ id: 'accessRoles.toast.deactivated', message: 'Role deactivated' })),
      );
    },
    onError: (error) => {
      if (isAccessRoleInUseError(error)) return;
      onError(error);
    },
  });
}
